import { motion } from "framer-motion";
import { Store, PackageCheck, Warehouse, CheckCircle2 } from "lucide-react";
import Section, { SectionHeading } from "@/components/Section";

const PILLARS = [
  {
    icon: Store,
    tag: "For QC Platforms",
    title: "Dark Store Management",
    text: "Manpower-led dark store operations with trained teams, supervisors, SOPs, attendance control, compliance support and SLA execution.",
    bullets: ["Store launch", "Picker-packer teams", "Supervision", "Daily MIS"],
  },
  {
    icon: PackageCheck,
    tag: "For FMCG & D2C Brands",
    title: "QC Marketplace Management",
    text: "Operational support for brands managing quick commerce platforms through catalog readiness, RO/PO tracking, inventory planning, replenishment and reporting.",
    bullets: ["Catalog readiness", "RO/PO tracking", "Inventory planning", "Sales reports"],
  },
  {
    icon: Warehouse,
    tag: "For Fast Commerce",
    title: "Warehousing & Fulfilment",
    text: "Regional warehousing, stock placement, order processing, dispatch coordination and last-mile support for faster Tier 2 and Tier 3 expansion.",
    bullets: ["Micro-warehousing", "Stock placement", "Dispatch", "Last-mile support"],
  },
];

function PillarCard({ s, i }) {
  const Icon = s.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: i * 0.08 }}
      data-testid={`pillar-card-${i}`}
      className="group rounded-[2rem] border border-neutral-200 bg-white p-7 lg:p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-2xl hover:border-[#99CC00]/40"
    >
      <div className="mb-7 flex items-start justify-between">
        <div className="rounded-2xl p-4" style={{ background: "rgba(153,204,0,0.14)" }}>
          <Icon className="h-8 w-8 text-[#99CC00]" />
        </div>
        <div className="rounded-full bg-neutral-100 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-neutral-500">
          {s.tag}
        </div>
      </div>
      <h3 className="text-2xl lg:text-3xl font-display font-black tracking-tight">{s.title}</h3>
      <p className="mt-4 min-h-[88px] text-base leading-7 text-neutral-600">{s.text}</p>
      <div className="mt-6 grid grid-cols-2 gap-2">
        {s.bullets.map((b) => (
          <div key={b} className="flex items-center gap-2 text-sm font-bold text-neutral-700">
            <CheckCircle2 className="h-4 w-4 text-[#99CC00]" /> {b}
          </div>
        ))}
      </div>
    </motion.article>
  );
}

export default function ServicePillars() {
  return (
    <Section id="service-pillars" bg="soft">
      <SectionHeading
        eyebrow="Service Pillars"
        title={<>One operating layer. <span className="text-[#99CC00]">Three growth engines.</span></>}
        sub="A single execution partner for store-floor operations, brand-side QC marketplace management and multi-city fulfilment."
      />
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {PILLARS.map((s, i) => <PillarCard key={s.title} s={s} i={i} />)}
      </div>
    </Section>
  );
}
